import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { requestMemberDeletion } from "../../features/portal/slice-extensions/portal-slice-extension";
import { clearPortalSession } from "../../features/portal/portalAxios";
import { fetchPortalErasurePreview } from "../../features/portal/slice-extensions/portal-erasure-slice-extension";
import { KeptTrainingRecord } from "../../features/members/models/MemberErasure";
import { trainingHoldSentences } from "../../features/members/utils/trainingHoldText";

interface Props {
  memberId: string;
  memberName: string;
}

/**
 * The member's right-to-erasure entry point: files a deletion request with the
 * instructor and ends the portal session immediately.
 */
const PortalDeleteDataCard = ({ memberId, memberName }: Props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [keptRecords, setKeptRecords] = useState<KeptTrainingRecord[]>([]);

  // The confirmation names any training record the school must keep (PD-0021).
  const startConfirming = async () => {
    setKeptRecords([]);
    setConfirming(true);
    const preview = await dispatch(fetchPortalErasurePreview(memberId));
    if (fetchPortalErasurePreview.fulfilled.match(preview)) {
      setKeptRecords(preview.payload);
    }
  };

  const handleRequest = async () => {
    setBusy(true);
    setError(false);
    try {
      await dispatch(requestMemberDeletion(memberId)).unwrap();
      setDone(true);
      clearPortalSession();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10">
        <p className="text-sm text-grey-20 dark:text-white">
          {t(
            "portal.deleteDone",
            "Your deletion request was sent to your instructor and you have been signed out.",
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10">
      <h2 className="text-base font-semibold text-grey-20 dark:text-white">
        {t("portal.deleteTitle", "Delete my data")}
      </h2>
      {!confirming ? (
        <>
          <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
            {t(
              "portal.deleteIntro",
              "Ask your instructor to delete the personal data held about {{name}}. Portal access ends immediately.",
              { name: memberName },
            )}
          </p>
          <button
            type="button"
            className="btn btn-secondary mt-3 text-red-600 dark:text-red-400"
            onClick={startConfirming}
          >
            {t("portal.deleteButton", "Request deletion…")}
          </button>
        </>
      ) : (
        <>
          <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
            {t(
              "portal.deleteConfirmText",
              "This signs you out of the portal right away and sends the request to your instructor. Continue?",
            )}
          </p>
          <p className="mt-2 text-sm text-grey-40 dark:text-grey-80">
            {t(
              "portal.deleteMessages",
              "Once your instructor carries it out, your messages are anonymised and the files you sent are deleted.",
            )}
          </p>
          {keptRecords.length > 0 && (
            <p className="mt-2 text-sm text-grey-40 dark:text-grey-80">
              {trainingHoldSentences(t, keptRecords, i18n.language, "portal.deleteTrainingHeld")}
            </p>
          )}
          <div className="mt-3 flex gap-2">
            <button type="button" className="btn btn-secondary" disabled={busy}
              onClick={() => setConfirming(false)}>
              {t("common.cancel", "Cancel")}
            </button>
            <button
              type="button"
              className="btn bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
              disabled={busy}
              onClick={handleRequest}
            >
              {busy
                ? t("portal.deleteSending", "Sending…")
                : t("portal.deleteConfirmButton", "Request deletion")}
            </button>
          </div>
        </>
      )}
      {error && (
        <p className="field-error mt-2">
          {t("portal.deleteFailed", "The request could not be sent. Please try again.")}
        </p>
      )}
    </div>
  );
};

export default PortalDeleteDataCard;
